/**
 * Pošta helpers — typ_zasilky labels, tones, and search/filter narrowing.
 *
 * The legacy module stores `typ_zasilky` as an integer: 0 = Odeslaná,
 * 1 = Přijatá. The list workspace exposes a 3-way subtab (Odeslaná / Přijatá /
 * Všechny); `Všechny` skips the filter entirely.
 */

import type { ZasilkaWithCarrier } from './api';

export type PostaTab = 'odeslana' | 'prijata' | 'vsechny';

export interface PostaSearch {
  tab?: PostaTab;
  search?: string;
  od?: string;
  do?: string;
}

const VALID_TABS = new Set<PostaTab>(['odeslana', 'prijata', 'vsechny']);

/** Validates the `?tab=&search=&od=&do=` query — default tab `odeslana` stays
 *  implicit in the URL so a bare `/posta` reads cleanly. */
export function parsePostaSearch(input: Record<string, unknown>): PostaSearch {
  const out: PostaSearch = {};
  if (typeof input.tab === 'string' && VALID_TABS.has(input.tab as PostaTab)) {
    out.tab = input.tab as PostaTab;
  }
  if (typeof input.search === 'string' && input.search.trim() !== '') {
    out.search = input.search;
  }
  if (typeof input.od === 'string' && input.od !== '') out.od = input.od;
  if (typeof input.do === 'string' && input.do !== '') out.do = input.do;
  return out;
}

export const POSTA_TAB_LABEL: Record<PostaTab, string> = {
  odeslana: 'Odeslaná',
  prijata: 'Přijatá',
  vsechny: 'Všechny',
};

export function typFromTab(tab: PostaTab): string | undefined {
  if (tab === 'odeslana') return '0';
  if (tab === 'prijata') return '1';
  return undefined;
}

export function typLabel(typ: number): string {
  return typ === 1 ? 'Přijatá' : 'Odeslaná';
}

export function typTone(typ: number): 'accent' | 'ok' {
  return typ === 1 ? 'accent' : 'ok';
}

/**
 * Client-side narrowing for the free-text search box.
 *
 * The API endpoint accepts `typ_zasilky` + date range only, so search across
 * popis / adresát / podací číslo / společnost happens in the browser against
 * the (capped) list response — same shape as Vozidla's URL-driven filters.
 */
export function matchesSearch(z: ZasilkaWithCarrier, needle: string): boolean {
  if (!needle) return true;
  const n = needle.trim().toLowerCase();
  if (!n) return true;
  return (
    z.popis_zasilky.toLowerCase().includes(n) ||
    z.adresat.toLowerCase().includes(n) ||
    z.podaci_cislo.toLowerCase().includes(n) ||
    (z.nazev?.toLowerCase().includes(n) ?? false)
  );
}
